import { firebaseDB } from "../firebase/firebase";
import * as Sentry from "@sentry/react";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { v4 as uuid4 } from "uuid";
import { ProviderUser } from "../../domain/types/ProviderUser";
import { FirestoreAppUserRepository } from "./appUserRepository";
import { FirestoreFamilyRepository } from "./FamilyRepository";
import { removeUndefinedProperties } from "../../domain/utils";

export const getFirestoreSafeProviderName = (providerName: string): string => {
  return providerName.replace(/[.#$[\]/]/g, "_");
};

const providerUsersCollection = collection(firebaseDB, "providerUsers");

export const createOrUpdateProviderUser = async (
  existingProviderUser: ProviderUser | null,
  newProviderUser: ProviderUser,
  options?: { forceNewAppUserId?: boolean; familyId?: string },
) => {
  console.debug(
    "createOrUpdateProviderUser called with existingProviderUser:",
    existingProviderUser,
  );
  console.debug(
    "createOrUpdateProviderUser called with newProviderUser:",
    newProviderUser,
  );
  const providerUserId = newProviderUser.uid;
  const appUserRepository = new FirestoreAppUserRepository();

  let appUserId: string | null = null;
  let familyId: string = uuid4();

  const providerName = getFirestoreSafeProviderName(
    newProviderUser.providerData[0].providerId,
  );

  if (options?.forceNewAppUserId) {
    appUserId = uuid4();
    familyId = options.familyId || familyId;

    console.log(
      "createOrUpdateProviderUser: Creating new appUserId due to forceNewAppUserId option",
    );

    // Link the new provider account to the new appUserId
    await appUserRepository.createOrUpdateAppUser(
      appUserId,
      providerName,
      providerUserId,
      familyId,
    );

    // Update or create the providerUser document
    const userRef = doc(firebaseDB, "providerUsers", providerUserId);
    await setDoc(
      userRef,
      removeUndefinedProperties({
        ...newProviderUser.toJSON(),
        googleAuthCode: newProviderUser.googleAuthCode,
        tokens: newProviderUser.tokens,
      }),
      { merge: true },
    );

    // If a familyId is provided in options, don't create a new family
    if (!options.familyId) {
      // Create a new family with the new user as admin
      const familyRepo = new FirestoreFamilyRepository();
      await familyRepo.create({
        familyId: familyId,
        members: [appUserId],
        admin: appUserId,
      });
    }

    Sentry.setUser({
      id: appUserId,
      email: newProviderUser.email || "email unknown",
    });

    return;
  }

  // Try to find appUserId associated with the newProviderUser
  const newAppUser = await appUserRepository.findByProviderUserId(
    providerName,
    providerUserId,
  );

  console.debug("createOrUpdateProviderUser: newAppUser:", newAppUser);
  console.debug("createOrUpdateProviderUser: providerName", providerName);
  console.debug("createOrUpdateProviderUser: providerUserId", providerUserId);

  if (newAppUser) {
    console.debug("createOrUpdateProviderUser: newAppUser found");
    appUserId = newAppUser.appUserId;
    familyId = newAppUser.familyId;
  } else if (existingProviderUser) {
    console.debug("createOrUpdateProviderUser: existingProviderUser found");
    // If not found, and existingProviderUser is present, try to find appUserId for existingProviderUser
    const existingProviderName = getFirestoreSafeProviderName(
      existingProviderUser.providerData[0].providerId,
    );

    console.debug(
      "createOrUpdateProviderUser: existingProviderName",
      existingProviderName,
    );
    console.debug(
      "createOrUpdateProviderUser: existingProviderUser.uid",
      existingProviderUser.uid,
    );

    const existingAppUser = await appUserRepository.findByProviderUserId(
      existingProviderName,
      existingProviderUser.uid,
    );

    console.debug(
      "createOrUpdateProviderUser: existingAppUser:",
      existingAppUser,
    );
    console.debug(
      "createOrUpdateProviderUser: existingProviderUser",
      existingProviderUser,
    );
    console.debug(
      "createOrUpdateProviderUser: existingProviderUser.uid",
      existingProviderUser.uid,
    );

    if (existingAppUser) {
      console.debug("createOrUpdateProviderUser: existingAppUser found");
      appUserId = existingAppUser.appUserId;
      familyId = existingAppUser.familyId;
    }
  }

  // If still not found, this is a brand-new user
  if (!appUserId) {
    appUserId = uuid4();
    console.log("createOrUpdateProviderUser: Brand new user");

    // Create a family and set new user as family admin
    const familyRepo = new FirestoreFamilyRepository();
    await familyRepo.create({
      familyId: familyId,
      members: [appUserId],
      admin: appUserId,
    });
  }

  console.debug("createOrUpdateProviderUser: appUserId:", appUserId);
  console.debug("createOrUpdateProviderUser: familyId:", familyId);
  console.debug("createOrUpdateProviderUser: providerName:", providerName);
  console.debug("createOrUpdateProviderUser: providerUserId:", providerUserId);

  // Link the new provider account to the appUserId
  await appUserRepository.createOrUpdateAppUser(
    appUserId,
    providerName,
    providerUserId,
    familyId,
  );

  const newProviderUserJSON = removeUndefinedProperties({
    ...newProviderUser.toJSON(),
    googleAuthCode: newProviderUser.googleAuthCode,
    tokens: newProviderUser.tokens,
    appUserId: appUserId,
  });
  console.debug(
    "createOrUpdateProviderUser: newProviderUserJSON():",
    newProviderUserJSON,
  );
  // Update or create the providerUser document
  const userRef = doc(firebaseDB, "providerUsers", providerUserId);
  await setDoc(userRef, newProviderUserJSON, { merge: true });
};
export const findByProviderUserId = async (
  providerUserId: string,
): Promise<ProviderUser | null> => {
  const providerUserRef = doc(providerUsersCollection, providerUserId);
  const providerUserDoc = await getDoc(providerUserRef);

  if (providerUserDoc.exists()) {
    return providerUserDoc.data() as ProviderUser;
  } else {
    console.warn(
      `No ProviderUser found with providerUserId: ${providerUserId}`,
    );
    return null;
  }
};

export const storeGoogleAuthCode = async (
  providerUserId: string,
  googleAuthCode: string,
) => {
  try {
    const userRef = doc(firebaseDB, "providerUsers", providerUserId);
    await setDoc(
      userRef,
      {
        googleAuthCode,
      },
      { merge: true },
    );
  } catch (e) {
    console.error("Error storing Google Auth Code: ", e);
  }
};

export const updateServices = async (
  providerUserId: string,
  service: string,
  value: boolean,
) => {
  try {
    const userRef = doc(firebaseDB, "providerUsers", providerUserId);
    await setDoc(
      userRef,
      {
        services: {
          [service]: value,
        },
      },
      { merge: true },
    );
  } catch (e) {
    console.error("Error updating providerUser Services: ", e);
  }
};

export const updateScopes = async (
  providerUserId: string,
  newScopes: string[],
) => {
  try {
    const userRef = doc(firebaseDB, "providerUsers", providerUserId);

    await updateDoc(userRef, {
      scopes: arrayUnion(...newScopes),
    });
  } catch (e) {
    console.error("Error updating providerUser Scopes: ", e);
    throw e;
  }
};

export const updateProviderUser = async (
  providerUserId: string,
  updates: {
    googleAuthCode?: string;
    services?: { [key: string]: boolean };
    scopes?: { [service: string]: string[] };
  }
) => {
  try {
    const userRef = doc(firebaseDB, "providerUsers", providerUserId);
    await setDoc(
      userRef,
      {
        ...updates
      },
      { merge: true }
    );
  } catch (e) {
    console.error("[providerUserRepository] Error updating provider user:", e);
    throw e;
  }
};
